import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

import { helpers } from '@/helpers.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import {
  INVALID_IMAGE_BOUNDS,
  INVALID_IMAGE_MIME_TYPE,
  LIKE_ALREADY_EXISTS_ERR_CODE,
  LIKE_NOT_AUTHORISED_ERR_CODE,
  LIKES_LIMIT_REACHED
} from '@/constants/error_codes.ts';

const appEnv = helpers.getEnvVar('VITE_APP_ENV', 'local');
export const isSentryEnabled = ['prod', 'stage'].includes(appEnv);

interface UserContext {
  hid: string;
  channel?: string;
}

interface ApiError {
  response?: {
    data?: {
      code?: number;
      message?: string;
    };
  };
}

const getUserContext = (): UserContext | null => {
  const aboutMe = useProfileStore.getState().aboutMe;

  if (!aboutMe?.profile) return null;

  return {
    hid: aboutMe.profile.hid,
    channel: aboutMe.real_time_channels?.main ?? undefined
  };
};

const formatErrorTrace = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const parts = [
      `AxiosError: ${error.message}`,
      `URL: ${error.config?.url}`,
      `Method: ${error.config?.method?.toUpperCase()}`,
      `Status: ${error.response?.status}`,
      `Response: ${JSON.stringify(error.response?.data)}`,
      error.stack
    ];
    return parts.filter(Boolean).join('\n');
  }

  if (error instanceof Error) {
    return error.stack || `${error.name}: ${error.message}`;
  }

  return String(error);
};

const extractErrorDetails = (error: unknown) => {
  if (error instanceof AxiosError) {
    return {
      type: 'AxiosError',
      message: error.message,
      code: error.code,
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      responseData: error.response?.data,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      requestData: error.config?.data
    };
  }

  if (error instanceof Error) {
    return {
      type: error.name,
      message: error.message
    };
  }

  return {
    type: typeof error,
    value: error
  };
};

export const logError = (
  error: unknown,
  context: Record<string, unknown> = {},
  sendToSentry: boolean = true
) => {
  const user = getUserContext();
  const errorDetails = extractErrorDetails(error);
  const errorTrace = formatErrorTrace(error);

  if (!sendToSentry || !isSentryEnabled) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        id: user.hid,
        channel: user.channel
      });
    }

    scope.setContext('errorDetails', errorDetails);
    scope.setContext('customContext', context);
    scope.setExtra('fullTrace', errorTrace);

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureException(new Error(String(error)));
    }
  });
};

export const catchErrors = (e: unknown, notify: boolean = false) => {
  const error = e as ApiError;
  const errorCode = error?.response?.data?.code;
  const errorMessage =
    error?.response?.data?.message ||
    (e instanceof Error ? e.message : String(e));

  // Skip logging for known, expected error codes
  const isExpectedError =
    errorCode === LIKE_ALREADY_EXISTS_ERR_CODE ||
    errorCode === LIKE_NOT_AUTHORISED_ERR_CODE ||
    errorCode === LIKES_LIMIT_REACHED;

  if (isExpectedError) {
    return;
  }

  logError(e, {
    source: 'catchErrors',
    errorCode,
    notify
  });

  if (notify) {
    const isNetworkError =
      !error.response &&
      e instanceof AxiosError &&
      (e.code === 'ERR_NETWORK' || e.message.toLowerCase().includes('network'));

    const isTimeoutError =
      !error.response &&
      e instanceof AxiosError &&
      e.code === 'ECONNABORTED' &&
      e.message.toLowerCase().includes('timeout');

    let msg = undefined;

    if (errorCode === INVALID_IMAGE_BOUNDS) {
      msg = 'toast.system.invalid_image_bounds';
    } else if (errorCode === INVALID_IMAGE_MIME_TYPE) {
      msg = 'toast.system.invalid_image_mime_type';
    } else if (isNetworkError) {
      msg = 'toast.system.network_error';
    } else if (isTimeoutError) {
      msg = 'toast.system.timeout_error';
    }

    if (msg) {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} msg={msg} />
      });
    }
  }

  throw e instanceof Error ? e : new Error(errorMessage);
};

export const logEvent = (
  message: string,
  data: Record<string, unknown> = {},
  level: 'info' | 'warning' | 'error' = 'info',
  sendToSentry: boolean = false
) => {
  const user = getUserContext();

  if (!sendToSentry || !isSentryEnabled) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        id: user.hid,
        channel: user.channel
      });
    }

    scope.setExtras(data);
    Sentry.captureMessage(message, level);
  });
};
